import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Scissors, Flame, Loader2, ArrowRight, AlertTriangle, Copy, Share2 } from "lucide-react";
import { getClipShare, registerClipView, trackReshare, mediaUrl } from "../lib/api";
import { toast } from "sonner";

export default function ClipShare() {
  const { clipId } = useParams();
  const [clip, setClip] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    getClipShare(clipId).then((c) => { setClip(c); setState("ready"); }).catch(() => setState("error"));
    registerClipView(clipId);
  }, [clipId]);

  const login = () => {
    localStorage.setItem("referrer_clip", clipId);
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const credit = clip?.creator ? ` by ${clip.creator.name || "@" + clip.creator.handle}` : "";
  const shareText = clip ? `"${clip.title}"${credit} — clipped with Clip Miner` : "";

  const doShare = (platform) => {
    trackReshare(clipId, platform);
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareText);
    const links = {
      x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      whatsapp: `https://wa.me/?text=${t}%20${u}`,
    };
    if (platform === "copy") {
      navigator.clipboard.writeText(shareUrl).then(() => toast.success("Link copied — creator credit included"));
      return;
    }
    if (platform === "native" && navigator.share) {
      navigator.share({ title: clip.title, text: shareText, url: shareUrl }).catch(() => {});
      return;
    }
    window.open(links[platform], "_blank", "noopener,width=600,height=600");
  };

  return (
    <div className="min-h-screen bg-background text-white" data-testid="clip-share-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <a href="/demo" className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center bg-accent"><Scissors className="h-4 w-4 text-black" /></div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">Clip Miner</span>
          </a>
          <button data-testid="share-cta-top" onClick={login}
                  className="flex items-center gap-2 bg-accent px-4 py-2 font-body text-xs font-semibold text-black transition-transform hover:-translate-y-0.5">
            Try it free <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      {state === "loading" && (
        <div className="flex h-[70vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-accent" /></div>
      )}
      {state === "error" && (
        <div className="mx-auto max-w-md px-6 py-24 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-primary" />
          <p className="mt-4 font-display text-2xl">Clip not found</p>
          <p className="mt-2 font-body text-sm text-muted-foreground">This link may have been removed.</p>
          <button onClick={login} className="mt-6 bg-accent px-6 py-3 font-body text-sm font-semibold text-black">Make your own clips</button>
        </div>
      )}
      {state === "ready" && clip && (
        <div className="mx-auto max-w-md px-6 py-10">
          <div className="overflow-hidden border border-border bg-black">
            <video src={mediaUrl(clip.video_path)} controls autoPlay playsInline
                   poster={clip.poster || clip.reframe_thumb ? mediaUrl(clip.poster || clip.reframe_thumb) : undefined}
                   className="aspect-[9/16] w-full bg-black" data-testid="share-video" />
          </div>
          <div className="mt-5">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 bg-accent px-2 py-0.5 font-mono text-[11px] font-bold text-black">
                <Flame className="h-3 w-3" /> {clip.funny_score}
              </span>
              {clip.show_title && <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{clip.show_title}</span>}
            </div>
            <h1 className="mt-3 font-display text-3xl tracking-wide">{clip.title}</h1>
            {/* Creator attribution */}
            {clip.creator && (
              <Link to={`/u/${clip.creator.handle}`} data-testid="creator-attribution"
                    className="mt-2 inline-flex items-center gap-1 font-mono text-xs text-accent hover:underline">
                by {clip.creator.name || "@" + clip.creator.handle} <ArrowRight className="h-3 w-3" />
              </Link>
            )}
            {clip.caption && <p className="mt-2 font-body text-sm text-muted-foreground">{clip.caption}</p>}
            {clip.hashtags?.length > 0 && (
              <p className="mt-2 font-mono text-xs text-accent">{clip.hashtags.join(" ")}</p>
            )}
          </div>

          {/* Social share — attributes the creator */}
          <div className="mt-6" data-testid="share-buttons">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Share this clip</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="share-x" onClick={() => doShare("x")}
                      className="flex items-center gap-2 border border-border px-3 py-2 font-body text-xs text-white transition-colors hover:border-accent hover:text-accent">
                <span className="font-display text-sm">X</span> Post
              </button>
              <button data-testid="share-facebook" onClick={() => doShare("facebook")}
                      className="border border-border px-3 py-2 font-body text-xs text-white transition-colors hover:border-accent hover:text-accent">Facebook</button>
              <button data-testid="share-whatsapp" onClick={() => doShare("whatsapp")}
                      className="border border-border px-3 py-2 font-body text-xs text-white transition-colors hover:border-accent hover:text-accent">WhatsApp</button>
              <button data-testid="share-copy" onClick={() => doShare("copy")}
                      className="flex items-center gap-1.5 border border-border px-3 py-2 font-body text-xs text-white transition-colors hover:border-accent hover:text-accent">
                <Copy className="h-3.5 w-3.5" /> Copy link
              </button>
              {typeof navigator !== "undefined" && navigator.share && (
                <button data-testid="share-native" onClick={() => doShare("native")}
                        className="flex items-center gap-1.5 border border-border px-3 py-2 font-body text-xs text-white transition-colors hover:border-accent hover:text-accent">
                  <Share2 className="h-3.5 w-3.5" /> More
                </button>
              )}
            </div>
            <p className="mt-2 font-body text-[11px] text-muted-foreground">Every share credits the creator{clip.creator ? ` @${clip.creator.handle}` : ""}.</p>
          </div>

          {clip.creator && (
            <Link to={`/u/${clip.creator.handle}`} data-testid="more-from-creator"
                  className="mt-6 flex items-center justify-between border border-accent/30 bg-accent/[0.05] px-5 py-4 transition-colors hover:bg-accent/[0.1]">
              <span className="font-body text-sm">More from <b>{clip.creator.name || "@" + clip.creator.handle}</b></span>
              <ArrowRight className="h-4 w-4 text-accent" />
            </Link>
          )}

          <div className="mt-6 border border-accent/30 bg-accent/[0.05] p-6 text-center">
            <p className="font-display text-xl">Made with Clip Miner</p>
            <p className="mx-auto mt-2 max-w-xs font-body text-sm text-muted-foreground">
              Turn your full sets into clips like this — automatically. Free to start.
            </p>
            <button data-testid="share-cta-bottom" onClick={login}
                    className="mt-5 inline-flex items-center gap-2 bg-accent px-6 py-3 font-body text-sm font-semibold text-black transition-transform hover:-translate-y-0.5">
              Try it free <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
